import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getCountryList, normalizeForSearch } from '../../countryData';
import { useT, useWidgetLanguage } from '../../i18n';
import { eventPathContains } from '../../utils';
import styles from './CountryPicker.module.css';

interface CountryPickerProps {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
}

export function CountryPicker({ value, onChange, placeholder, className, hasError }: CountryPickerProps) {
  const t = useT();
  // Country names render in the visitor's language; the selection is the ISO
  // code either way, so switching language never changes what is submitted.
  const countryList = getCountryList(useWidgetLanguage());
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedCountry = useMemo(
    () => countryList.find((c) => c.code === value),
    [countryList, value]
  );

  const filtered = useMemo(() => {
    if (!search) return countryList;
    const q = normalizeForSearch(search);
    return countryList.filter(
      (c) => normalizeForSearch(c.name).includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [countryList, search]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearch('');
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  const handleSelect = useCallback(
    (code: string) => {
      onChange(code);
      setIsOpen(false);
      setSearch('');
    },
    [onChange]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!eventPathContains(e, containerRef.current)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className={`${className || ''} ${styles.trigger} ${hasError ? styles.hasError : ''}`}
      >
        {selectedCountry ? (
          <span className={styles.selectedValue}>
            <span className={styles.flag}>{selectedCountry.flag}</span>
            {selectedCountry.name}
          </span>
        ) : (
          <span className={styles.placeholder}>{placeholder || t('form.selectCountry')}</span>
        )}
        <svg className={styles.chevron} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrapper}>
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('form.searchCountries')}
              className={styles.searchInput}
            />
          </div>
          <ul className={styles.optionsList}>
            {filtered.length === 0 ? (
              <li className={styles.noResults}>{t('form.noCountries')}</li>
            ) : (
              filtered.map((country) => (
                <li key={country.code}>
                  <button
                    type="button"
                    className={`${styles.option} ${country.code === value ? styles.optionSelected : ''}`}
                    onClick={() => handleSelect(country.code)}
                  >
                    <span className={styles.flag}>{country.flag}</span>
                    <span className={styles.optionName}>{country.name}</span>
                    <span className={styles.optionCode}>{country.code}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
